import axios from 'axios'
import Adapter from 'axios-mock-adapter'
import { get } from 'lodash'
import util from '@/libs/util'
import { dataNotFound, errorCreate, errorLog } from './tools'
import router from '@/router'
import qs from 'qs'
import Cookies from 'js-cookie'
import store from '@/store/index'
import { getConfigJson } from '@/views/contactPerson/api'
import auth from '@/utils/auth' // 导入Token管理服务

/**
 * @description 创建请求实例
 */
axios.defaults.retry = 1
axios.defaults.retryDelay = 1000
axios.defaults.withCredentials = true

let refreshing = false
const queue = []

export function getErrorMessage(msg) {
   if (typeof msg === 'string') {
      return msg
   }
   if (typeof msg === 'object') {
      if (msg.code === 'token_not_valid') {
         util.cookies.remove('token')
         util.cookies.remove('uuid')
         router.push({ path: '/login' })
         router.go(0)
         return '登录超时，请重新登录！'
      }
      if (msg.code === 'user_not_found') {
         util.cookies.remove('token')
         util.cookies.remove('uuid')
         router.push({ path: '/login' })
         router.go(0)
         return '用户无效，请重新登录！'
      }
      return Object.values(msg)
   }
   if (Object.prototype.toString.call(msg).slice(8, -1) === 'Array') {
      return msg
   }
   return msg
}

function createService() {
   // 创建一个 axios 实例
   const service = axios.create({
      baseURL: util.baseURL(),
      timeout: 20000,
      paramsSerializer: (params) => qs.stringify(params, { indices: false }),
   })
   
   // 请求拦截
   service.interceptors.request.use(
      async (config) => {
         // 记录请求信息，便于调试（保留简洁的日志）
         const requestMethod = config.method.toUpperCase();
         const requestUrl = config.url;
         console.log(`请求: ${requestMethod} ${requestUrl}`);

         try {
            // 1. 添加客户端ID
            await addClientId(config);
            
            // 2. 添加认证信息（Token）
            // await addAuthToken(config);
            
            // 3. 添加会话信息（Session Key）
            addSessionInfo(config);
            
            return config;
         } catch (error) {
            console.error('请求拦截器处理失败:', error);
            return config; // 即使处理失败也继续请求
         }
      },
      (error) => {
         console.log('请求发送失败:', error);
         return Promise.reject(error);
      }
   )
   
   // 响应拦截
   service.interceptors.response.use(
      (response) => {
         // 处理常规响应
         let dataAxios = response.data
         if (response.headers['content-disposition']) {
            dataAxios = response
         }
         
         // 检查响应中是否有Token错误 
         // if (dataAxios && 
         //     (dataAxios.status_code === -10009 || // Token验证失败的状态码
         //      dataAxios.message === 'ACCESS_TOKEN凭证验证失败。')) {
            
         //    // 刷新Token并重试请求
         //    return auth.refreshToken().then(() => {
         //       // 更新请求中的token
         //       const newToken = util.cookies.get('Access_Token')
         //       response.config.params.access_token = window.decodeURIComponent(newToken)
               
         //       // 重新发送请求
         //       return service(response.config)
         //    })
         // }
         
         return dataAxios
      },
      (error) => {
         const status = get(error, 'response.status')
            switch (status) {
               case 400:
                  error.message = '请求错误'
                  break
               case 401:
                  error.message = '未认证，请登录'
                  break
               case 403:
                  error.message = '拒绝访问'
                  break
               case 404:
                  error.message = `请求地址出错: ${error.response?.config.url}`
                  break
               case 408:
                  error.message = '请求超时'
                  break
               case 500:
                  error.message = '服务器内部错误'
                  break
               case 501:
                  error.message = '服务未实现'
                  break
               case 502:
                  error.message = '网关错误'
                  break
               case 503:
                  error.message = '服务不可用'
                  break
               case 504:
                  error.message = '网关超时'
                  break
               case 505:
                  error.message = 'HTTP版本不受支持'
                  break
               default:
                  break
            }
            errorLog(error)
            return Promise.reject(error)
      }
   )
   return service
}

/**
 * @description 创建请求方法
 * @param {Object} service axios 实例
 */
function createRequestFunction(service) {
   // 校验是否为租户模式。租户模式把域名替换成 域名 加端口
   return function (config) {
      let baseURL = ''
      if (process.env.NODE_ENV === 'development') {
         baseURL = `${location.origin}/api/tccc`
      } else {
         baseURL = `${location.origin}/tccc`
      }

      const token = util.cookies.get('token')
      // 进行布尔值兼容
      var params = get(config, 'params', {})
      for (const key of Object.keys(params)) {
         if (String(params[key]) === 'true') {
            params[key] = 1
         }
         if (String(params[key]) === 'false') {
            params[key] = 0
         }
      }

      const configDefault = {
         headers: {
            Authorization: 'JWT ' + token,
            'Content-Type': get(
               config,
               'headers.Content-Type',
               'application/json'
            ),
         },
         timeout: 60000,
         baseURL: baseURL,
         data: {},
         params: params,
      }
      return service(Object.assign(configDefault, config))
   }
}

// 用于真实网络请求的实例和请求方法
export const service = createService()
export const request = createRequestFunction(service)

// 用于模拟网络请求的实例和请求方法
export const serviceForMock = createService()
export const requestForMock = createRequestFunction(serviceForMock)

// 网络请求数据模拟工具
export const mock = new Adapter(serviceForMock)

// 修改：使用新的auth服务刷新Token
const refreshTken = async function () {
   try {
      return await auth.refreshToken()
   } catch (error) {
      console.error('Token刷新失败:', error)
      return Promise.reject(error)
   }
}

/**
 * @description 请求前端配置文件
 * @param {Object} context
 */
const get_config_json = async function () {
   let res = await getConfigJson()
   for (let i = 0; i < res.client.length; i++) {
      if (res.client[i].client_id == client_id) {
         // 缓存app_secret appid
         util.localStorage.set('app_secret', res.client[i].app_secret)
         util.localStorage.set('appid', res.client[i].appid)
         return res
      }
   }
}

/**
 * 下载文件
 * @param url
 * @param params
 * @param method
 * @param filename
 */
export const downloadFile = function ({ url, params, method, filename }) {
   request({
      url: url,
      method: method,
      params: params,
      responseType: 'blob',
      // headers: {Accept: 'application/vnd.openxmlformats-officedocument'}
   }).then((res) => {
      const fileName =
         window.decodeURI(res.headers['content-disposition'].split('=')[1]) ||
         filename + '.xls' ||
         '文件导出.xls'
      if (res) {
         const blob = new Blob([res.data], { type: 'charset=utf-8' })
         const elink = document.createElement('a')
         elink.download = fileName
         elink.style.display = 'none'
         elink.href = URL.createObjectURL(blob)
         document.body.appendChild(elink)
         elink.click()
         URL.revokeObjectURL(elink.href) // 释放URL 对象0
         document.body.removeChild(elink)
      }
   })
}

/**
 * 添加客户端ID到请求参数
 * @param {Object} config - 请求配置对象
 */
async function addClientId(config) {
   const client_id = util.localStorage.get('client_id');
   
   if (!client_id) {
      return; // 没有客户端ID则跳过
   }
   
   if (['post', 'put', 'delete'].includes(config.method)) {
      // 添加到请求体
      config.data = {
         ...config.data,
         client_id,
      };
   } else if (config.method === 'get') {
      // 添加到URL参数
      config.params = {
         ...config.params,
         client_id,
      };
   }
}

/**
 * 添加认证Token到请求
 * @param {Object} config - 请求配置对象
 */
async function addAuthToken(config) {
   // 跳过token获取接口
   if (config.url && config.url.indexOf('get_access_token') !== -1) {
      return;
   }
   
   try {
      // 如果Token未初始化，则等待初始化
      if (!util.cookies.get('Access_Token')) {
         console.log('请求需要Token但未初始化，等待初始化');
         await auth.initTokenService('axios.interceptor');
      }
      
      // 从cookie获取token
      const appid = util.cookies.get('appid');
      const access_token = util.cookies.get('Access_Token');
      
      // 只有当获取到token时才添加
      if (appid || access_token) {
         // 添加到请求参数
         config.params = {
            ...config.params,
            appid: appid ? window.decodeURIComponent(appid) : '',
            access_token: access_token ? window.decodeURIComponent(access_token) : '',
            type: 'stdaln',
            force_check: 'true'
         };
         
         // 简化日志输出
         const hasAppId = !!appid;
         const hasToken = !!access_token;
         console.log(`Token状态: appid(${hasAppId ? '✓' : '✗'}) token(${hasToken ? '✓' : '✗'})`);
      }
   } catch (error) {
      console.error('获取认证Token失败:', error);
      // 失败时不阻止请求继续
   }
}

/**
 * 添加会话信息到请求
 * @param {Object} config - 请求配置对象
 */
function addSessionInfo(config) {
   const appid = util.localStorage.get('appid');
   const session_key = util.localStorage.get('session_key');
   
   // 如果没有session_key，记录一条警告但不阻止请求
   if (!session_key) {
      console.warn('请求缺少session_key，某些功能可能不可用');
   }
   
   // 添加到请求参数中
   if (appid || session_key) {
      config.params = {
         ...config.params,
         ...(session_key && { session_key }),
         ...(appid && { appid }),
      };
      
      // 简化日志
      const hasSessionKey = !!session_key;
      const hasAppId = !!appid;
      console.log(`会话状态: session_key(${hasSessionKey ? '✓' : '✗'}) appid(${hasAppId ? '✓' : '✗'})`);
   }
}
